import React, { useState, useMemo, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Person, FileText } from "react-bootstrap-icons";
import { OverlayTrigger, Popover, Tooltip } from "react-bootstrap";
import I_icon from "../../../assets/I_icon.png";
import { toast } from "react-toastify";
import history_icon from "../../../assets/history_icon.png";

import { useTranslation } from "react-i18next";

export default function CandidatePool({
  candidates,
  selectedIds,
  setSelectedIds,
  selectAllRequestModel,
  setSelectAllRequestModel,
  selectedPositionIds = [],
  onViewFile,
  loading,
  page,
  pageSize,
  filters,
  totalElements,
  onPageChange,
  onPageSizeChange,
  selectedPositionId,
  requisition,
  position,
  selectedRequisitionId,
  isRankEnabled,
  hasLocationData,
  allCandidatesForFilters,
  isMarksUploaded,
  onApprovalHistory,
  workflowStatus,
}) {
  const { t } = useTranslation(["candidateWorkflow", "common"]);
  const STATUS_CLASS_MAP = {
    Applied: "bg-secondary",
    Shortlisted: "bg-warning",
    Discrepancy: "bg-primary",
    Rejected: "bg-danger",
    Pending: "bg-info",
  };
  const WORKFLOW_STATUS_CLASS_MAP = {
    Pending: "bg-info",
    "L1 Pending": "bg-warning",
    Approved: "bg-success",
    Rejected: "bg-danger",
    Published: "bg-success",
  };

  const navigate = useNavigate();
  const [sortConfig, setSortConfig] = useState({ key: null, direction: null });

  /* ---------- Selection logic ---------- */

  const allSelected = selectAllRequestModel?.selectAll === true;

  useEffect(() => {
    if (!filters?.status?.length) {
      setSelectedIds([]);
    }
    setSelectAllRequestModel(null);
  }, [filters?.status, setSelectedIds, setSelectAllRequestModel]);

  const formatStatus = (status = "") =>
    status
      .toLowerCase()
      .split("_")
      .map((word) => {
        if (word === "l1" || word === "l2") {
          return word.toUpperCase();
        }
        return word.charAt(0).toUpperCase() + word.slice(1);
      })
      .join(" ");

  const toggleSelectAll = () => {
    // 1. If everything is already selected, ALWAYS allow clearing selections
    if (allSelected) {
      setSelectedIds([]);
      setSelectAllRequestModel(null);
      return;
    }

    if (!filters?.status?.includes("SHORTLISTED")) {
      toast.error("Please select the status filter first");
      return;
    }

    const requestModel = {
      selectAll: true,
      positionIds: selectedPositionIds,
      selectedIds: [],
      statusList: ["SHORTLISTED"],
      excludedIds: [],
    };
    console.info("[CandidatePool] Select-all payload created", requestModel);
    setSelectedIds([]);
    setSelectAllRequestModel(requestModel);

    toast.success(
      `${formatStatus("SHORTLISTED")} candidates selected`
    );
  };

  const toggleRow = (id) => {
    if (allSelected) {
      setSelectAllRequestModel((current) => {
        if (!current) return current;
        const excludedIds = current.excludedIds || [];
        return {
          ...current,
          excludedIds: excludedIds.includes(id)
            ? excludedIds.filter((excludedId) => excludedId !== id)
            : [...excludedIds, id],
        };
      });
      return;
    }

    setSelectedIds((prev) =>
      prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]
    );
  };

  /* ---------- Sorting logic ---------- */

  const requestSort = (key) => {
    setSortConfig((prev) => {
      if (prev.key === key) {
        return {
          key,
          direction: prev.direction === "asc" ? "desc" : "asc",
        };
      }
      return { key, direction: "asc" };
    });
  };

  const sortedCandidates = useMemo(() => {
    if (!sortConfig.key) return candidates;

    return [...candidates].sort((a, b) => {
      const aVal = a[sortConfig.key];
      const bVal = b[sortConfig.key];

      if (aVal == null) return 1;
      if (bVal == null) return -1;

      if (typeof aVal === "number" && typeof bVal === "number") {
        return sortConfig.direction === "asc" ? aVal - bVal : bVal - aVal;
      }

      return sortConfig.direction === "asc"
        ? String(aVal).localeCompare(String(bVal))
        : String(bVal).localeCompare(String(aVal));
    });
  }, [candidates, sortConfig]);

  const sortIcon = (key) => {
    if (sortConfig.key !== key) return "↕";
    return sortConfig.direction === "asc" ? "▲" : "▼";
  };
  const getLevelFrom100 = (val) => {
    if (val >= 75) return { label: "Strong", color: "green" };
    if (val >= 50) return { label: "Moderate", color: "orange" };
    return { label: "Weak", color: "red" };
  };
  const posStageWorkflowId = candidates[0]?.posStageWorkflowId;
  const renderPopover = (c) => {
    const scoreMeta = getLevelFrom100(c.finalScore);
    return (
      <Popover className="rank-popover">
        <div className="rank-header">
          {t("candidateWorkflow:candidate_analysis")} - {c.name}
        </div>

        <div className="rank-body">
          <div className="final-score">
            <p className="m-0 p-0 greenfin">
              {t("candidateWorkflow:final_score")}
            </p>

            <div className="score">{c.finalScore}%</div>

            <div className={`score-label ${scoreMeta.color}`}>
              {t(`candidateWorkflow:${scoreMeta.label.toLowerCase()}`)}
            </div>
          </div>

          <hr />

          <div className="section">
            <div className="section-header">
              <span>{t("candidateWorkflow:score_details")}</span>
              <span className="weight-header">
                {t("candidateWorkflow:weightage")}
              </span>
            </div>

            <div className="item">
              <span className="dot green"></span>

              <span className="label">
                {t("candidateWorkflow:education")}:{" "}
                <strong>{c.educationScore}%</strong>
              </span>

              <span className="weight">10%</span>
            </div>

            <div className="item">
              <span className="dot green"></span>

              <span className="label">
                {t("candidateWorkflow:experience")}:{" "}
                <strong>{c.experienceScore}%</strong>
              </span>

              <span className="weight">10%</span>
            </div>
          </div>

          <hr />

          <div className="section">
            <div className="section-header">
              <span>{t("candidateWorkflow:areas_for_review")}</span>
              <span className="weight-header">
                {t("candidateWorkflow:weightage")}
              </span>
            </div>

            <div className="item">
              <span className="dot yellow"></span>

              <span className="label">
                {t("candidateWorkflow:education_similarity")}:{" "}
                <strong>{c.educationSimilarity}%</strong>
              </span>

              <span className="weight">20%</span>
            </div>

            <div className="item">
              <span className="dot yellow"></span>

              <span className="label">
                {t("candidateWorkflow:experience_similarity")}:{" "}
                <strong>{c.experienceSimilarity}%</strong>
              </span>

              <span className="weight">60%</span>
            </div>
          </div>
        </div>
      </Popover>
    );
  };
  return (
    <>
      {/* Desktop Table */}
      <div className="card-body p-0 d-none d-md-block">
        <table className="table table-hover mb-0">
          <thead className="bg-light">
            <tr>
              <th
                className="fs-14 fw-normal py-3"
                style={{ paddingLeft: "1rem" }}
              >
                <input
                  type="checkbox"
                  checked={allSelected}
                  onChange={toggleSelectAll}
                />
              </th>

              <th
                className="fs-14 fw-normal py-3"
                onClick={() => requestSort("name")}
                role="button"
              >
                {t("candidateWorkflow:candidate")} {sortIcon("name")}
              </th>

              <th className="fs-14 fw-normal py-3" role="button">
                {t("common:rank")}
              </th>

              <th
                className="fs-14 fw-normal py-3"
                onClick={() => requestSort("score")}
                role="button"
              >
                {t("common:score")} {sortIcon("score")}
              </th>

              <th
                className="fs-14 fw-normal py-3"
                onClick={() => requestSort("experienceMonths")}
                role="button"
              >
                {t("candidateWorkflow:experience")}{" "}
                {sortIcon("experienceMonths")}
              </th>

              <th className="fs-14 fw-normal py-3">
                {t("candidateWorkflow:status")}
              </th>

              <th className="fs-14 fw-normal py-3">
                {t("candidateWorkflow:approvalstatus")}

                {["L1 Pending", "Approved", "Published", "Rejected"].includes(
                  workflowStatus
                ) && (
                  <OverlayTrigger
                    placement="top"
                    overlay={<Tooltip>Approval History</Tooltip>}
                  >
                    <img
                      src={history_icon}
                      alt="history_icon"
                      className="cursor-pointer ms-1"
                      style={{ width: "16px", height: "16px" }}
                      onClick={() => onApprovalHistory(posStageWorkflowId)}
                    />
                  </OverlayTrigger>
                )}
              </th>

              {hasLocationData && (
                <th className="fs-14 fw-normal py-3">{t("common:location")}</th>
              )}

              <th className="fs-14 fw-normal py-3">{t("common:category")}</th>

              {isMarksUploaded && (
                <th className="fs-14 fw-normal py-3">
                  {t("common:total_marks_obtained")}
                </th>
              )}

              {isMarksUploaded && (
                <th className="fs-14 fw-normal py-3">
                  {t("common:exam_status")}
                </th>
              )}

              <th className="text-center fs-14 fw-normal py-3">
                {t("common:actions")}
              </th>
            </tr>
          </thead>

          <tbody>
            {loading ? (
              <tr>
                <td colSpan="10" className="text-center py-4">
                  {t("candidateWorkflow:loading_candidates")}
                </td>
              </tr>
            ) : sortedCandidates.length === 0 ? (
              <tr>
                <td colSpan="11" className="text-center py-4">
                  {t("candidateWorkflow:no_candidates_found")}
                </td>
              </tr>
            ) : (
              sortedCandidates.map((c) => (
                <tr key={c.id}>
                  <td
                    className="align-content-center"
                    style={{ paddingLeft: "1rem" }}
                  >
                    <input
                      type="checkbox"
                      checked={
                        allSelected
                          ? !selectAllRequestModel.excludedIds?.includes(c.id)
                          : selectedIds.includes(c.id)
                      }
                      onChange={() => toggleRow(c.id)}
                    />
                  </td>

                  <td className="align-content-center">
                    <p className="fw-normal fs-14 mb-0">{c.name}</p>
                    <p className="text-muted fs-12 mb-0">
                      {t("candidateWorkflow:application_number")}:{" "}
                      {c.applicationNo}
                    </p>
                    <p className="text-muted fs-12 mb-0">
                      {t("candidateWorkflow:position")}:{" "}
                      {position?.find((p) => p.positionId === c.positionId)
                        ?.positionName || "-"}
                    </p>
                  </td>

                  <td className="align-content-center">
                    <p className="fw-normal fs-14 mb-0">{c?.rank || "-"}</p>
                  </td>

                  <td className="align-content-center">
                    <div className="rank-cell">
                      <span className="rank-value fw-normal fs-14 mb-0">
                        {c?.finalScore || "-"}
                      </span>

                      {isRankEnabled && (
                        <OverlayTrigger
                          trigger="click"
                          placement="auto"
                          rootClose
                          overlay={renderPopover(c)}
                          popperConfig={{
                            modifiers: [
                              {
                                name: "offset",
                                options: {
                                  offset: [0, 12],
                                },
                              },
                            ],
                          }}
                        >
                          <img
                            src={I_icon}
                            alt="info_icon"
                            className="info-icon"
                          />
                        </OverlayTrigger>
                      )}
                    </div>
                  </td>

                  <td className="align-content-center">
                    {/* <p className="fw-normal fs-14 mb-0">{(c.experienceMonths / 12).toFixed(1)} {t("candidateWorkflow:years")}</p> */}
                    <p className="fw-normal fs-14 mb-0">
                      {((c.experienceMonths ?? 0) / 12).toFixed(1)}{" "}
                      {t("candidateWorkflow:years")}
                    </p>
                  </td>

                  <td className="align-content-center">
                    <span
                      className={`round_badge px-3 py-1 fs-12 rounded text-white ${
                        STATUS_CLASS_MAP[c.status] || "bg-secondary"
                      }`}
                    >
                      {c.status}
                    </span>
                  </td>
                  <td className="align-content-center">
                    <span
                      className={`round_badge px-3 py-1 fs-12 rounded text-white ${
                        WORKFLOW_STATUS_CLASS_MAP[c.workflowStatus] ||
                        "bg-secondary"
                      }`}
                    >
                      {c.workflowStatus || "-"}
                    </span>
                  </td>

                  {hasLocationData && (
                    <td className="align-content-center">
                      <p className="fw-normal fs-14 mb-0">{c.location}</p>
                    </td>
                  )}

                  <td className="align-content-center">
                    <p className="fw-normal fs-14 mb-0">{c.categoryName}</p>
                  </td>

                  {isMarksUploaded && (
                    <td className="align-content-center">
                      <p className="fw-normal fs-14 mb-0">
                        {c.totalMarksObtained ?? "-"}
                      </p>
                    </td>
                  )}

                  {isMarksUploaded && (
                    <td className="align-content-center">
                      <p className="fw-normal fs-14 mb-0">
                        {c.examQualificationStatus || "-"}
                      </p>
                    </td>
                  )}

                  <td className="text-center align-content-center">
                    <OverlayTrigger
                      placement="bottom"
                      overlay={
                        <Tooltip id={`tooltip-${c.id}`}>
                          {t("candidateWorkflow:view_profile")}
                        </Tooltip>
                      }
                    >
                      <Person
                        size={16}
                        className="me-3 cursor-pointer"
                        onClick={() => {
                          navigate("/candidate-preview", {
                            state: {
                              from: "/candidate-workflow",
                              candidate: c,
                              workflowStatus: c.workflowStatus,
                              positionId: selectedPositionId, // for preview API
                              positionIds: selectedPositionId, // for auto populate after back
                              candidatePositionId: c.positionId,
                              requisitionId: selectedRequisitionId,
                              requisition: requisition
                                ? {
                                    requisition_code:
                                      requisition.requisition_code,
                                    requisition_title:
                                      requisition.requisition_title,
                                    registration_start_date:
                                      requisition.registration_start_date,
                                    registration_end_date:
                                      requisition.registration_end_date,
                                  }
                                : null,
                              position:
                                position?.map?.((p) => ({
                                  positionId: p.positionId,
                                  positionName: p.positionName,
                                  isLocationWise: p.isLocationWise,
                                })) || [],
                              activeTab: "CANDIDATE_POOL",
                              isRankEnabled,

                              //ADD THESE
                              page,
                              pageSize,
                              filters,
                            },
                          });
                        }}
                      />
                    </OverlayTrigger>
                    <OverlayTrigger
                      placement="bottom"
                      overlay={
                        <Tooltip id={`tooltip-${c.id}`}>
                          {t("candidateWorkflow:view_resume")}
                        </Tooltip>
                      }
                    >
                      <FileText
                        size={16}
                        className="cursor-pointer"
                        onClick={() => onViewFile(c)}
                      />
                    </OverlayTrigger>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>

        {/* Pagination */}
        <div className="d-flex justify-content-between align-items-center px-3 py-3 border-top">
          <div className="fs-14 text-muted">
            {t("candidateWorkflow:showing")} {page * pageSize + 1}–
            {Math.min((page + 1) * pageSize, totalElements)}{" "}
            {t("candidateWorkflow:of")} {totalElements}
          </div>

          <div className="d-flex align-items-center gap-2">
            <select
              className="form-select fs-14"
              style={{ width: "90px" }}
              value={pageSize}
              onChange={(e) => {
                onPageSizeChange(Number(e.target.value));
                onPageChange(0);
              }}
            >
              {[10, 20, 50].map((s) => (
                <option key={s} value={s}>
                  {s}
                </option>
              ))}
            </select>

            <button
              className="btn btn-sm btn-outline-secondary"
              disabled={page === 0}
              onClick={() => onPageChange(page - 1)}
            >
              {t("candidateWorkflow:prev")}
            </button>

            <button
              className="btn btn-sm btn-outline-secondary"
              disabled={(page + 1) * pageSize >= totalElements}
              onClick={() => onPageChange(page + 1)}
            >
              {t("candidateWorkflow:next")}
            </button>
          </div>
        </div>
      </div>

      {/* Mobile Cards (NO checkboxes here on purpose) */}
      <div className="d-md-none">
        {candidates.map((c) => (
          <div key={c.id} className="card mb-3">
            <div className="card-body">
              <h6 className="fw-bold mb-1">{c.name}</h6>
              <small className="text-muted d-block mb-2">
                {t("candidateWorkflow:application_number")}: {c.regNo}
              </small>

              <div className="mb-1">
                <strong>{t("candidateWorkflow:experience")}:</strong>
                {((c.experienceMonths ?? 0) / 12).toFixed(1)}{" "}
                {t("candidateWorkflow:years")}
                {/* <strong>{t("candidateWorkflow:experience")}:</strong> {(c.experienceMonths / 12).toFixed(1)} {t("candidateWorkflow:years")} */}
              </div>
              <div className="mb-1">
                <strong>{t("candidateWorkflow:status")}:</strong>{" "}
                <span
                  className={`round_badge px-3 py-1 fs-12 rounded text-white ${
                    STATUS_CLASS_MAP[c.status] || "bg-secondary"
                  }`}
                >
                  {/* {c.status} */}
                  {t(`candidateWorkflow:status_${c.status?.toLowerCase()}`, {
                    defaultValue: c.status,
                  })}
                </span>
              </div>
              <div className="mb-1">
                <strong>{t("common:location")}:</strong> {c.location}
              </div>
              <div className="mb-2">
                <strong>{t("common:category")}:</strong> {c.categoryName}
              </div>

              <div className="d-flex gap-2">
                <Person
                  size={16}
                  className="me-3 cursor-pointer"
                  onClick={() =>
                    navigate("/candidate-preview", {
                      
                      state: {
                        from: "/candidate-workflow",
                        isRankEnabled,
                        //  ADD
                        activeTab: "CANDIDATE_POOL",
                        page,
                        pageSize,
                        filters,
                        candidate: c,
                        workflowStatus: c.workflowStatus,
                        positionId: selectedPositionId,
                        requisitionId: selectedRequisitionId,
                        requisition: requisition
                          ? {
                              requisition_code: requisition.requisition_code,
                              requisition_title: requisition.requisition_title,
                              registration_start_date:
                                requisition.registration_start_date,
                              registration_end_date:
                                requisition.registration_end_date,
                            }
                          : null,
                        position: position
                          ? {
                              positionId: position.positionId,
                              positionName: position.positionName,
                              isLocationWise: position.isLocationWise,
                            }
                          : null,
                      },
                    })
                  }
                />
                <FileText
                  className="cursor-pointer"
                  onClick={() => onViewFile(c)}
                />
              </div>
            </div>
          </div>
        ))}
      </div>
    </>
  );
}
